'use client';
import { useState } from 'react';
import emailjs from '@emailjs/browser';

interface ShipperFormData {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    message: string;
}

interface CarrierFormData {
    name: string;
    mcNumber: string;
    phone: string;
    email: string;
    message: string;
}

interface FormErrors {
    firstName?: string;
    lastName?: string;
    name?: string;
    mcNumber?: string;
    phone?: string;
    email?: string;
    message?: string;
}

const ContactForm = () => {
    const [shipperData, setShipperData] = useState<ShipperFormData>({
        firstName: '',
        lastName: '',
        phone: '',
        email: '',
        message: ''
    });

    const [carrierData, setCarrierData] = useState<CarrierFormData>({
        name: '',
        mcNumber: '',
        phone: '',
        email: '',
        message: ''
    });

    const [shipperErrors, setShipperErrors] = useState<FormErrors>({});
    const [carrierErrors, setCarrierErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState<{
        type: 'success' | 'error' | null;
        message: string;
    }>({ type: null, message: '' });

    const validateShipperForm = (): boolean => {
        const errors: FormErrors = {};

        if (!shipperData.firstName.trim()) errors.firstName = 'First name is required';
        if (!shipperData.lastName.trim()) errors.lastName = 'Last name is required';
        if (!shipperData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(shipperData.email)) {
            errors.email = 'Please enter a valid email address';
        }
        if (shipperData.phone.trim() && !/^\+?[\d\s-]{10,}$/.test(shipperData.phone)) {
            errors.phone = 'Please enter a valid phone number';
        }
        if (!shipperData.message.trim()) errors.message = 'Message is required';

        setShipperErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validateCarrierForm = (): boolean => {
        const errors: FormErrors = {};

        if (!carrierData.name.trim()) errors.name = 'Name is required';
        if (!carrierData.mcNumber.trim()) errors.mcNumber = 'MC# is required';
        if (!carrierData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(carrierData.email)) {
            errors.email = 'Please enter a valid email address';
        }
        if (carrierData.phone.trim() && !/^\+?[\d\s-]{10,}$/.test(carrierData.phone)) {
            errors.phone = 'Please enter a valid phone number';
        }
        if (!carrierData.message.trim()) errors.message = 'Message is required';

        setCarrierErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleShipperSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateShipperForm()) return;

        setIsSubmitting(true);
        setSubmitStatus({ type: null, message: '' });

        try {
            const templateParams = {
                to_email: 'cs@junctionil.com',
                from_name: `${shipperData.firstName} ${shipperData.lastName}`,
                from_email: shipperData.email,
                phone: shipperData.phone,
                message: shipperData.message
            };

            await emailjs.send(
                'YOUR_SERVICE_ID',
                'YOUR_TEMPLATE_ID',
                templateParams,
                'YOUR_PUBLIC_KEY'
            );

            setSubmitStatus({
                type: 'success',
                message: 'Message sent successfully! We will get back to you soon.'
            });

            setShipperData({
                firstName: '',
                lastName: '',
                phone: '',
                email: '',
                message: ''
            });
        } catch (error) {
            setSubmitStatus({
                type: 'error',
                message: 'Failed to send message. Please try again later.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCarrierSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!validateCarrierForm()) return;

        setIsSubmitting(true);
        setSubmitStatus({ type: null, message: '' });

        try {
            const templateParams = {
                to_email: 'procurement@junctionil.com',
                from_name: carrierData.name,
                mc_number: carrierData.mcNumber,
                from_email: carrierData.email,
                phone: carrierData.phone,
                message: carrierData.message
            };

            await emailjs.send(
                'YOUR_SERVICE_ID',
                'YOUR_TEMPLATE_ID',
                templateParams,
                'YOUR_PUBLIC_KEY'
            );

            setSubmitStatus({
                type: 'success',
                message: 'Message sent successfully! We will get back to you soon.'
            });

            setCarrierData({
                name: '',
                mcNumber: '',
                phone: '',
                email: '',
                message: ''
            });
        } catch (error) {
            setSubmitStatus({
                type: 'error',
                message: 'Failed to send message. Please try again later.'
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="py-20 mt-20 bg-white pt-50">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Shippers Form */}
                <div className="mb-16">
                    <div className="text-center mb-8">
                        <div className="inline-block px-4 py-2 bg-[#29abe2] bg-opacity-10 rounded-full mb-4">
                            <span className="text-[#29abe2] font-semibold">Ship with us</span>
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">
                            Shippers
                        </h2>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Partner with us for reliable and efficient shipping solutions. Fill out the form below and our team will get back to you shortly.
                        </p>
                    </div>

                    <div className="bg-gray-50 p-8 rounded-xl shadow-sm">
                        <form onSubmit={handleShipperSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="shipper-firstName" className="block text-sm font-medium text-gray-700 mb-2">
                                        First Name *
                                    </label>
                                    <input
                                        id="shipper-firstName"
                                        type="text"
                                        value={shipperData.firstName}
                                        onChange={(e) => setShipperData({...shipperData, firstName: e.target.value})}
                                        placeholder="Enter your first name"
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-[#29abe2] focus:border-[#29abe2] ${
                                            shipperErrors.firstName ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    />
                                    {shipperErrors.firstName && (
                                        <p className="mt-1 text-sm text-red-600">{shipperErrors.firstName}</p>
                                    )}
                                </div>
                                <div>
                                    <label htmlFor="shipper-lastName" className="block text-sm font-medium text-gray-700 mb-2">
                                        Last Name *
                                    </label>
                                    <input
                                        id="shipper-lastName"
                                        type="text"
                                        value={shipperData.lastName}
                                        onChange={(e) => setShipperData({...shipperData, lastName: e.target.value})}
                                        placeholder="Enter your last name"
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-[#29abe2] focus:border-[#29abe2] ${
                                            shipperErrors.lastName ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    />
                                    {shipperErrors.lastName && (
                                        <p className="mt-1 text-sm text-red-600">{shipperErrors.lastName}</p>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="shipper-phone" className="block text-sm font-medium text-gray-700 mb-2">
                                        Phone
                                    </label>
                                    <input
                                        id="shipper-phone"
                                        type="tel"
                                        value={shipperData.phone}
                                        onChange={(e) => setShipperData({...shipperData, phone: e.target.value})}
                                        placeholder="Enter your phone number"
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-[#29abe2] focus:border-[#29abe2] ${
                                            shipperErrors.phone ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    />
                                    {shipperErrors.phone && (
                                        <p className="mt-1 text-sm text-red-600">{shipperErrors.phone}</p>
                                    )}
                                </div>
                                <div>
                                    <label htmlFor="shipper-email" className="block text-sm font-medium text-gray-700 mb-2">
                                        Email *
                                    </label>
                                    <input
                                        id="shipper-email"
                                        type="email"
                                        value={shipperData.email}
                                        onChange={(e) => setShipperData({...shipperData, email: e.target.value})}
                                        placeholder="Enter your email address"
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-[#29abe2] focus:border-[#29abe2] ${
                                            shipperErrors.email ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    />
                                    {shipperErrors.email && (
                                        <p className="mt-1 text-sm text-red-600">{shipperErrors.email}</p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label htmlFor="shipper-message" className="block text-sm font-medium text-gray-700 mb-2">
                                    Message *
                                </label>
                                <textarea
                                    id="shipper-message"
                                    rows={4}
                                    value={shipperData.message}
                                    onChange={(e) => setShipperData({...shipperData, message: e.target.value})}
                                    placeholder="Tell us about your shipping needs"
                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-[#29abe2] focus:border-[#29abe2] ${
                                        shipperErrors.message ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                ></textarea>
                                {shipperErrors.message && (
                                    <p className="mt-1 text-sm text-red-600">{shipperErrors.message}</p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`w-full bg-[#29abe2] text-white px-8 py-4 rounded-lg transition duration-300 ${
                                    isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-[#1d8ab3]'
                                }`}
                            >
                                {isSubmitting ? 'Sending...' : 'Send Message'}
                            </button>
                        </form>
                    </div>
                </div>

                {/* Carriers Form */}
                <div className="relative">
                    {/* <div className="absolute inset-0 bg-gradient-to-b from-white to-transparent h-12"></div> */}
                    <div className="text-center mb-8">
                        <div className="inline-block px-4 py-2 bg-[#29abe2] bg-opacity-10 rounded-full mb-4">
                            <span className="text-[#29abe2] font-semibold">Drive with us</span>
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-4">
                            Carriers
                        </h2>
                        <p className="text-gray-600 max-w-2xl mx-auto">
                            Join our network of trusted carriers. Complete the form below to start the partnership process.
                        </p>
                    </div>

                    <div className="bg-[#052c65] bg-opacity-5 p-8 rounded-xl shadow-sm">
                        <form onSubmit={handleCarrierSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="carrier-name" className="block text-sm font-medium text-gray-700 mb-2">
                                        Name *
                                    </label>
                                    <input
                                        id="carrier-name"
                                        type="text"
                                        value={carrierData.name}
                                        onChange={(e) => setCarrierData({...carrierData, name: e.target.value})}
                                        placeholder="Enter your full name"
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-[#052c65] focus:border-[#052c65] ${
                                            carrierErrors.name ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    />
                                    {carrierErrors.name && (
                                        <p className="mt-1 text-sm text-red-600">{carrierErrors.name}</p>
                                    )}
                                </div>
                                <div>
                                    <label htmlFor="carrier-mc" className="block text-sm font-medium text-gray-700 mb-2">
                                        MC# *
                                    </label>
                                    <input
                                        id="carrier-mc"
                                        type="text"
                                        value={carrierData.mcNumber}
                                        onChange={(e) => setCarrierData({...carrierData, mcNumber: e.target.value})}
                                        placeholder="Enter your MC number"
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-[#052c65] focus:border-[#052c65] ${
                                            carrierErrors.mcNumber ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    />
                                    {carrierErrors.mcNumber && (
                                        <p className="mt-1 text-sm text-red-600">{carrierErrors.mcNumber}</p>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label htmlFor="carrier-phone" className="block text-sm font-medium text-gray-700 mb-2">
                                        Phone
                                    </label>
                                    <input
                                        id="carrier-phone"
                                        type="tel"
                                        value={carrierData.phone}
                                        onChange={(e) => setCarrierData({...carrierData, phone: e.target.value})}
                                        placeholder="Enter your phone number"
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-[#052c65] focus:border-[#052c65] ${
                                            carrierErrors.phone ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    />
                                    {carrierErrors.phone && (
                                        <p className="mt-1 text-sm text-red-600">{carrierErrors.phone}</p>
                                    )}
                                </div>
                                <div>
                                    <label htmlFor="carrier-email" className="block text-sm font-medium text-gray-700 mb-2">
                                        Email *
                                    </label>
                                    <input
                                        id="carrier-email"
                                        type="email"
                                        value={carrierData.email}
                                        onChange={(e) => setCarrierData({...carrierData, email: e.target.value})}
                                        placeholder="Enter your email address"
                                        className={`w-full px-4 py-3 border rounded-lg focus:ring-[#052c65] focus:border-[#052c65] ${
                                            carrierErrors.email ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                    />
                                    {carrierErrors.email && (
                                        <p className="mt-1 text-sm text-red-600">{carrierErrors.email}</p>
                                    )}
                                </div>
                            </div>

                            <div>
                                <label htmlFor="carrier-message" className="block text-sm font-medium text-gray-700 mb-2">
                                    Message *
                                </label>
                                <textarea
                                    id="carrier-message"
                                    rows={4}
                                    value={carrierData.message}
                                    onChange={(e) => setCarrierData({...carrierData, message: e.target.value})}
                                    placeholder="Tell us about your services and experience"
                                    className={`w-full px-4 py-3 border rounded-lg focus:ring-[#052c65] focus:border-[#052c65] ${
                                        carrierErrors.message ? 'border-red-500' : 'border-gray-300'
                                    }`}
                                ></textarea>
                                {carrierErrors.message && (
                                    <p className="mt-1 text-sm text-red-600">{carrierErrors.message}</p>
                                )}
                            </div>

                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={`w-full bg-[#052c65] text-white px-8 py-4 rounded-lg transition duration-300 ${
                                    isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:bg-[#041e46]'
                                }`}
                            >
                                {isSubmitting ? 'Sending...' : 'Send Message'}
                            </button>
                        </form>
                    </div>
                </div>

                {submitStatus.type && (
                    <div 
                        className={`mt-6 p-4 rounded-lg text-center ${
                            submitStatus.type === 'success' 
                                ? 'bg-green-50 text-green-800' 
                                : 'bg-red-50 text-red-800'
                        }`}
                    >
                        {submitStatus.message}
                    </div>
                )}
            </div>
        </section>
    );
};

export default ContactForm; 